import React from 'react';
import { Icon } from '../ui';

/**
 * Refined underline tab bar for the project-detail view. 36px tall,
 * 24px horizontal padding, --bg-surface, 1px --border-subtle bottom.
 *
 * Per Schedule handoff §2 "Tab Bar":
 *   - Inactive tabs: 12.5px / 500 / --text-3
 *   - Active tab: 12.5px / 600 / --text-1 + a 2px --accent underline
 *     rendered at bottom: -1px so it overlaps the bar's own border.
 *   - No hover background.
 *   - "Client View" is NOT a tab — it's a permissions-context pill
 *     rendered right-aligned. It still navigates to the client-view
 *     tab; the pill styling just calls out the read-only mode.
 *
 * Props:
 *   tabs            [{ key, label }, ...] — standard tabs only.
 *                   Filter Client View out of this list before passing.
 *   activeTab       Current key (matches tabs[].key OR 'client view')
 *   onChange(key)   Click handler
 *   showClientView  Whether to show the right-aligned Client View pill
 *   onClientView()  Optional handler when the pill is clicked (defaults
 *                   to onChange('client view'))
 */
export default function TabBar({
  tabs,
  activeTab,
  onChange,
  showClientView = false,
  onClientView,
}) {
  return (
    <div
      style={{
        height: 36, // 44 → 36 (Duncan 2026-10-27: the top of the page took "almost half
                    // of the screen"); the chips keep 4px above and below
        display: 'flex',
        alignItems: 'stretch',
        gap: 4,
        padding: '0 32px',  // 32px breathing room matches ProjectHeader + KpiStrip
        background: 'var(--bg-surface)',
        borderLeft: '1px solid var(--border-subtle)',
        borderRight: '1px solid var(--border-subtle)',
        borderBottom: '1px solid var(--border-subtle)',
        // Rounded bottom corners so ProjectHeader + TabBar visually
        // close as a single card; the 8px --bg-page gutter below
        // separates this block from the KpiStrip card.
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        position: 'sticky',
        top: 48, // sits directly under ProjectHeader
        zIndex: 99,
        fontFamily: 'var(--font-sans)',
        // Tabs lay out left-to-right with whiteSpace: nowrap labels.
        // Without overflow handling they'd push the whole chrome past
        // the viewport on narrower windows (cutting off the last tab
        // + everything in the right-side header / KPI strip). overflow-x:
        // auto keeps the chrome capped at viewport width and lets users
        // scroll the bar horizontally when the project has more tabs
        // than fit. overflow-y: visible so the bottom underline + sticky
        // positioning still work cleanly.
        overflowX: 'auto', overflowY: 'visible',
      }}
    >
      {tabs.map((tab) => (
        <TabButton
          key={tab.key}
          label={tab.label}
          active={activeTab === tab.key}
          onClick={() => onChange(tab.key)}
        />
      ))}

      {showClientView && (
        <div
          style={{
            marginLeft: 'auto',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ClientViewPill
            active={activeTab === 'client view'}
            onClick={
              onClientView || (() => onChange('client view'))
            }
          />
        </div>
      )}
    </div>
  );
}

/* -------------------- TabButton -------------------- */

/**
 * Chip-style tab button per Overview handoff Part 1. Every tab is a
 * soft rounded chip — inactive on --bg-surface-2 with a subtle border,
 * active on --accent-soft with a 22%-mix accent ring + the 3px sage
 * underline bleeding through the bar's bottom border.
 *
 * Reverses our earlier "underline-only" treatment because the bare
 * underline tabs read too cramped against the KPI strip below.
 * Also the tabs inside a sub's card on the Subcontractors tab (Duncan
 * 2026-10-22: "like the tabs in the Project screen itself").
 */
export function TabButton({ label, active, onClick }) {
  const [hover, setHover] = React.useState(false);
  const inactiveBg = hover ? 'var(--bg-hover)' : 'var(--bg-surface-2)';
  const inactiveColor = hover ? 'var(--text-1)' : 'var(--text-2)';
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        padding: '0 12px',
        margin: '4px 0', // gives the chip vertical breathing room inside the 36px bar
        border: '1px solid',
        borderColor: active
          ? 'color-mix(in oklch, var(--accent) 22%, transparent)'
          : 'var(--border-subtle)',
        background: active ? 'var(--accent-soft)' : inactiveBg,
        color: active ? 'var(--text-1)' : inactiveColor,
        fontSize: 14,
        fontWeight: active ? 600 : 500,
        letterSpacing: '-0.005em',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        borderRadius: 7,
        transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease',
      }}
    >
      {label}
      {active && (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            // Bleeds through the bar's own border-bottom (-4px: the chip's
            // 4px margin, so the 3px stripe lands flush with the underside),
            // tying the chip to the content below.
            bottom: -4,
            height: 3,
            background: 'var(--accent)',
            borderRadius: 2,
          }}
        />
      )}
    </button>
  );
}

/* -------------------- Client View pill -------------------- */

function ClientViewPill({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        height: 28,
        padding: '0 10px',
        borderRadius: 6,
        background: 'var(--accent-soft)',
        border: `1px solid ${
          active
            ? 'var(--accent)'
            : 'color-mix(in oklch, var(--accent) 22%, transparent)'
        }`,
        color: 'var(--text-2)',
        fontFamily: 'var(--font-sans)',
        fontSize: 13.5,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'border-color .12s, background .12s',
      }}
      title="View as client (read-only)"
    >
      <Icon name="eye" size={13} />
      <span>Client View</span>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 3,
          padding: '1px 6px',
          borderRadius: 999,
          background: 'color-mix(in oklch, var(--accent) 15%, transparent)',
          color: 'var(--accent-text)',
          fontSize: 11.5,
          fontWeight: 500,
        }}
      >
        <Icon name="lock" size={10} />
        Read-only
      </span>
    </button>
  );
}
